import { isChunkError, reloadOnceForChunkError } from "@/utils/chunkReload";
import { createRouter, createWebHistory } from "vue-router";

// Keep layouts and auth pages eager-loaded for faster initial auth check
import LayoutDefault from "@/layouts/LayoutDefault";
import LayoutLogin from "@/layouts/LayoutLogin";
import LayoutOnboarding from "@/layouts/LayoutOnboarding";
import Login from "@/views/Login";
import Logout from "@/views/Logout";

// Lazy-load all other views for better performance
const BusinessForm = () => import(/* webpackChunkName: "business" */ "@/views/Business/BusinessForm");
const BusinessMain = () => import(/* webpackChunkName: "business" */ "@/views/Business/BusinessMain");
const AdminPanel = () => import(/* webpackChunkName: "admin" */ "@/views/AdminPanel");
const Notifications = () => import(/* webpackChunkName: "business" */ "@/views/Notifications");
const Share = () => import(/* webpackChunkName: "share" */ "@/views/Share");
const Referral = () => import(/* webpackChunkName: "business" */ "@/views/Referral");
const Trash = () => import(/* webpackChunkName: "business" */ "@/views/Trash");
const BusinessSubscription = () => import(/* webpackChunkName: "business" */ "@/views/Business/BusinessSubscription");
const BusinessSecurity = () => import(/* webpackChunkName: "business" */ "@/views/Business/BusinessSecurity");
const BusinessUsers = () => import(/* webpackChunkName: "business" */ "@/views/Business/BusinessUsers/BusinessUsers");
const BusinessUsersAddUpdate = () => import(/* webpackChunkName: "business" */ "@/views/Business/BusinessUsers/BusinessUsersAddUpdate");

const CustomersAddUpdate = () => import(/* webpackChunkName: "customers" */ "@/views/Customers/CustomersAddUpdate");
const CustomersDetails = () => import(/* webpackChunkName: "customers" */ "@/views/Customers/CustomersDetails");
const Customers = () => import(/* webpackChunkName: "customers-list" */ "@/views/Customers/Customers");

const ForgotPassword = () => import(/* webpackChunkName: "auth" */ "@/views/ForgotPassword");
const ResetPassword = () => import(/* webpackChunkName: "auth" */ "@/views/ResetPassword");
const Signup = () => import(/* webpackChunkName: "auth" */ "@/views/Signup");

const NoteTemplatesAddUpdate = () => import(/* webpackChunkName: "note-templates" */ "@/views/NoteTemplates/NoteTemplatesAddUpdate");
const NoteTemplates = () => import(/* webpackChunkName: "note-templates" */ "@/views/NoteTemplates/NoteTemplates");

const Dashboard = () => import(/* webpackChunkName: "dashboard" */ "@/views/Dashboard");

const TaskBoardView = () => import(/* webpackChunkName: "tasks" */ "@/views/Tasks/TaskBoardView");
const TaskCreateView = () => import(/* webpackChunkName: "tasks" */ "@/views/Tasks/TaskCreateView");

const Calendar = () => import(/* webpackChunkName: "calendar" */ "@/views/Calendar");
const Agenda = () => import(/* webpackChunkName: "agenda" */ "@/views/Agenda");
const PublicAppointment = () => import(/* webpackChunkName: "public-booking" */ "@/views/PublicAppointment");
const MagicLogin = () => import(/* webpackChunkName: "public-booking" */ "@/views/MagicLogin");
const PublicBooking = () => import(/* webpackChunkName: "public-booking" */ "@/views/PublicBooking");
const Financeiro = () => import(/* webpackChunkName: "agenda" */ "@/views/Financeiro");

const Reports = () => import(/* webpackChunkName: "reports" */ "@/views/Reports/Reports");
const CustomerReport = () => import(/* webpackChunkName: "reports" */ "@/views/Reports/CustomerReport");

const ActivityHistory = () => import(/* webpackChunkName: "activity" */ "@/views/ActivityHistory");

const NotFound = () => import(/* webpackChunkName: "common" */ "@/views/NotFound");

const OnboardingPersonalProfile = () => import(/* webpackChunkName: "onboarding" */ "@/views/Onboarding/OnboardingPersonalProfile");
const OnboardingSpecialty = () => import(/* webpackChunkName: "onboarding" */ "@/views/Onboarding/OnboardingSpecialty");
const OnboardingReady = () => import(/* webpackChunkName: "onboarding" */ "@/views/Onboarding/OnboardingReady");

const PasswordChange = () => import(/* webpackChunkName: "profile" */ "@/views/PersonalProfile/PasswordChange");
const AccountSecurity = () => import(/* webpackChunkName: "profile" */ "@/views/PersonalProfile/AccountSecurity");
const PersonalProfile = () => import(/* webpackChunkName: "profile" */ "@/views/PersonalProfile/PersonalProfile");

const PrintLayout = () => import(/* webpackChunkName: "print" */ "@/views/PrintLayout");
const SubscriptionSuspended = () => import(/* webpackChunkName: "auth" */ "@/views/SubscriptionSuspended");

import Helpers from "@/helpers";

const helpers = Helpers;

const routes = [
  {
    name: "Logout",
    path: "/logout",
    component: Logout,
  },
  {
    name: "Signup",
    path: "/signup",
    component: Signup,
  },
  {
    name: "PublicAppointment",
    path: "/consulta/:token",
    component: PublicAppointment,
    meta: { public: true },
  },
  {
    name: "MagicLogin",
    path: "/entrar/:token",
    component: MagicLogin,
    meta: { public: true },
  },
  {
    name: "InstallApp",
    path: "/instalar",
    component: () => import(/* webpackChunkName: "install-app" */ "@/views/InstallApp"),
    meta: { public: true },
  },
  {
    name: "VideoRoom",
    path: "/sala/:token",
    component: () => import(/* webpackChunkName: "video-room" */ "@/views/VideoRoom"),
    meta: { public: true },
  },
  {
    name: "PublicBreathing",
    path: "/respirar/:pattern",
    component: () => import(/* webpackChunkName: "bilateral" */ "@/views/PublicBreathing"),
    meta: { public: true },
  },
  {
    name: "PublicTask",
    path: "/tarefa/:token",
    component: () => import(/* webpackChunkName: "public-booking" */ "@/views/PublicTask"),
    meta: { public: true },
  },
  {
    name: "PublicSign",
    path: "/assinar/:token",
    component: () => import(/* webpackChunkName: "public-booking" */ "@/views/PublicSign"),
    meta: { public: true },
  },
  {
    name: "PublicScale",
    path: "/escala/:token",
    component: () => import(/* webpackChunkName: "public-booking" */ "@/views/PublicScale"),
    meta: { public: true },
  },
  {
    name: "PublicBilateral",
    path: "/estimulo/:token",
    component: () => import(/* webpackChunkName: "bilateral" */ "@/views/PublicBilateral"),
    meta: { public: true },
  },
  {
    name: "PublicIntake",
    path: "/ficha/:token",
    component: () => import(/* webpackChunkName: "public-booking" */ "@/views/PublicIntake"),
    meta: { public: true },
  },
  {
    name: "PublicBooking",
    path: "/agendar/:code",
    component: PublicBooking,
    meta: { public: true },
  },
  {
    name: "SubscriptionSuspended",
    path: "/subscription-suspended",
    component: SubscriptionSuspended,
  },
  {
    path: "/",
    component: LayoutLogin,
    children: [
      { path: "/", redirect: "/login", meta: { requiresAuth: true } },
      {
        path: "/login",
        name: "Login",
        component: Login,
        meta: { requiresAuth: false },
      },
      {
        path: "/forgot-password",
        name: "ForgotPassword",
        component: ForgotPassword,
        meta: { requiresAuth: false },
      },
      {
        path: "/reset-password",
        name: "ResetPassword",
        component: ResetPassword,
        meta: { requiresAuth: false },
      },
    ],
  },
  {
    path: "/",
    component: LayoutOnboarding,
    children: [
      {
        path: "/onboarding",
        redirect: "/onboarding/specialty",
        meta: { requiresAuth: true },
      },
      // Removed - old 4-step onboarding flow
      // {
      //   path: "/onboarding/welcome",
      //   name: "OnboardingWelcome",
      //   component: OnboardingWelcome,
      //   meta: { requiresAuth: true },
      // },
      {
        path: "/onboarding/personal-profile",
        name: "OnboardingPersonalProfile",
        component: OnboardingPersonalProfile,
        meta: { requiresAuth: true },
      },
      {
        path: "/onboarding/specialty",
        name: "OnboardingSpecialty",
        component: OnboardingSpecialty,
        meta: { requiresAuth: true },
      },
      {
        path: "/onboarding/ready",
        name: "OnboardingReady",
        component: OnboardingReady,
        meta: { requiresAuth: true },
      },
    ],
  },
  {
    path: "/",
    component: LayoutDefault,
    children: [
      { path: "/", redirect: "/dashboard", meta: { requiresAuth: true } },
      {
        path: "/dashboard",
        name: "Dashboard",
        component: Dashboard,
        meta: { requiresAuth: true },
      },

      {
        path: "/business",
        redirect: "/business/main",
        meta: { requiresAuth: true, rules: { subject: "Company" } },
      },
      {
        path: "/business/form",
        name: "BusinessForm",
        component: BusinessForm,
        meta: { requiresAuth: true, rules: { subject: "CustomerField" } },
      },
      {
        path: "/business/main",
        name: "BusinessMain",
        component: BusinessMain,
        meta: { requiresAuth: true, rules: { subject: "Company" } },
      },
      {
        path: "/lixeira",
        name: "Trash",
        component: Trash,
        meta: { requiresAuth: true },
      },
      // Files shared to the installed app (manifest share_target → service worker → here), or picked here
      {
        path: "/compartilhar",
        name: "Share",
        component: Share,
        meta: { requiresAuth: true },
      },
      {
        path: "/notificacoes",
        name: "Notifications",
        component: Notifications,
        meta: { requiresAuth: true },
      },
      { path: "/referral", redirect: "/indique" },
      // Deep link from e-mails: /assinatura?plano=consultorio opens the checkout with the plan chosen.
      { path: "/assinatura", redirect: (to) => ({ name: "BusinessSubscription", query: to.query }) },
      {
        path: "/indique",
        name: "Referral",
        component: Referral,
        meta: { requiresAuth: true },
      },
      {
        path: "/business/subscription",
        name: "BusinessSubscription",
        component: BusinessSubscription,
        meta: { requiresAuth: true, rules: { subject: "Company" } },
      },
      {
        path: "/business/security",
        name: "BusinessSecurity",
        component: BusinessSecurity,
        meta: { requiresAuth: true, rules: { subject: "Company" } },
      },
      {
        path: "/business/users",
        name: "BusinessUsers",
        component: BusinessUsers,
        meta: { requiresAuth: true, rules: { subject: "User" } },
      },
      {
        path: "/business/users/add",
        name: "BusinessUsersAdd",
        component: BusinessUsersAddUpdate,
        meta: { requiresAuth: true, rules: { subject: "User" } },
      },
      {
        path: "/business/users/edit/:id",
        name: "BusinessUsersEdit",
        component: BusinessUsersAddUpdate,
        meta: {
          requiresAuth: true,
          rules: { requiredActions: ["edit", "manage"], subject: "User" },
        },
      },

      {
        path: "/customers",
        name: "Customers",
        component: Customers,
        meta: { requiresAuth: true, rules: { subject: "Customer" } },
      },
      {
        path: "/ferramentas",
        name: "Tools",
        component: () => import("@/views/Tools/Tools.vue"),
        meta: { requiresAuth: true },
      },
      {
        path: "/ferramentas/genograma",
        name: "ToolGenogram",
        component: () => import("@/views/Tools/ToolGenogram.vue"),
        meta: { requiresAuth: true },
      },
      {
        path: "/ferramentas/respiracao",
        name: "ToolBreathing",
        component: () => import(/* webpackChunkName: "bilateral" */ "@/views/Tools/ToolBreathing.vue"),
        meta: { requiresAuth: true },
      },
      {
        path: "/ferramentas/escalas",
        name: "ToolScales",
        component: () => import("@/views/Tools/ToolScales.vue"),
        meta: { requiresAuth: true },
      },
      {
        path: "/ferramentas/estimulacao-bilateral",
        name: "ToolBilateral",
        component: () => import(/* webpackChunkName: "bilateral" */ "@/views/Tools/ToolBilateral.vue"),
        meta: { requiresAuth: true },
      },
      {
        path: "/meu-site",
        name: "MySite",
        component: () => import("@/views/MySite.vue"),
        meta: { requiresAuth: true },
      },
      {
        path: "/importar",
        name: "DataImport",
        component: () => import("@/views/DataImport.vue"),
        meta: { requiresAuth: true, rules: { subject: "Customer" } },
      },
      {
        path: "/customers/add",
        name: "CustomersAdd",
        component: CustomersAddUpdate,
        meta: { requiresAuth: true, rules: { subject: "Customer" } },
      },
      {
        path: "/customers/edit/:id",
        name: "CustomersEdit",
        component: CustomersAddUpdate,
        meta: { requiresAuth: true, rules: { subject: "Customer" } },
      },
      {
        path: "/customers/details/:id",
        name: "CustomersDetails",
        component: CustomersDetails,
        meta: { requiresAuth: true, rules: { subject: "Customer" } },
      },
      {
        path: "/customers/:id",
        redirect: (to) => {
          return { name: "CustomersDetails", params: { id: to.params.id }, query: to.query };
        },
        meta: { requiresAuth: true },
      },

      {
        path: "/note-templates",
        name: "NoteTemplates",
        component: NoteTemplates,
        meta: { requiresAuth: true, rules: { subject: "NoteTemplate" } },
      },
      {
        path: "/note-templates/add",
        name: "NoteTemplatesAdd",
        component: NoteTemplatesAddUpdate,
        meta: { requiresAuth: true, rules: { subject: "NoteTemplate" } },
      },
      {
        path: "/note-templates/edit/:id",
        name: "NoteTemplatesEdit",
        component: NoteTemplatesAddUpdate,
        meta: { requiresAuth: true, rules: { subject: "NoteTemplate" } },
      },

      {
        path: "/tasks",
        name: "TaskBoards",
        component: TaskBoardView,
        meta: { requiresAuth: true },
      },
      {
        path: "/tasks/add",
        name: "TasksAdd",
        component: TaskCreateView,
        meta: { requiresAuth: true },
      },
      {
        path: "/tasks/board/:id",
        name: "TaskBoardView",
        component: TaskBoardView,
        meta: { requiresAuth: true },
      },

      {
        path: "/agenda",
        name: "Agenda",
        component: Agenda,
        meta: { requiresAuth: true },
      },
      {
        // Google sends the person back here after "Conectar com Google" (GoogleCalendar on the API)
        path: "/agenda/google",
        name: "AgendaGoogle",
        component: Agenda,
        meta: { requiresAuth: true },
      },

      {
        path: "/gestao",
        name: "AdminPanel",
        component: AdminPanel,
        meta: { requiresAuth: true },
      },
      {
        path: "/financeiro",
        name: "Financeiro",
        component: Financeiro,
        meta: { requiresAuth: true },
      },

      {
        path: "/calendar",
        name: "Calendar",
        component: Calendar,
        meta: { requiresAuth: true },
      },

      {
        path: "/reports",
        name: "Reports",
        component: Reports,
        meta: { requiresAuth: true },
      },
      {
        path: "/reports/customers",
        name: "CustomerReport",
        component: CustomerReport,
        meta: { requiresAuth: true },
      },

      {
        path: "/activity-history",
        name: "ActivityHistory",
        component: ActivityHistory,
        meta: { requiresAuth: true },
      },

      {
        path: "/personal-profile",
        redirect: "/personal-profile/personal-data",
        meta: { requiresAuth: true },
      },
      {
        path: "/personal-profile/personal-data",
        name: "PersonalProfile",
        component: PersonalProfile,
        meta: { requiresAuth: true },
      },
      {
        path: "/personal-profile/password",
        name: "PasswordChange",
        component: PasswordChange,
        meta: { requiresAuth: true },
      },
      {
        path: "/personal-profile/security",
        name: "AccountSecurity",
        component: AccountSecurity,
        meta: { requiresAuth: true },
      },

      {
        path: "/print-layout",
        name: "PrintLayout",
        component: PrintLayout,
        meta: { requiresAuth: true },
      },

      {
        path: "/access-denied",
        name: "AccessDenied",
        component: NotFound,
        meta: { requiresAuth: true },
      },

      {
        path: "/not-found",
        name: "NotFound",
        component: NotFound,
        meta: { requiresAuth: true },
      },

      {
        path: "/:pathMatch(.*)*",
        name: "NotFound",
        component: NotFound,
        meta: { requiresAuth: true },
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach((to, from, next) => {
  if (
    to.redirectedFrom &&
    to.redirectedFrom.path &&
    to.redirectedFrom.path.substring(0, 2) === "/#"
  ) {
    const path = to.redirectedFrom.path.substring(2);
    return next({ path, replace: true });
  }

  if (to.matched.some((record) => record.meta.requiresAuth)) {
    const accessToken = localStorage.getItem("ordexa/headers/access-token");
    const client = localStorage.getItem("ordexa/headers/client");
    const contentType = localStorage.getItem("ordexa/headers/content-type");
    const expiry = localStorage.getItem("ordexa/headers/expiry");
    const tokenType = localStorage.getItem("ordexa/headers/token-type");
    const uid = localStorage.getItem("ordexa/headers/uid");

    helpers.checkFrontendSessionExpiry({ route: to, router, next });

    if (accessToken && client && contentType && expiry && tokenType && uid) {
      // Password-change deadline passed: only the "new password" screen until it is changed.
      let user = null;
      try {
        user = JSON.parse(localStorage.getItem("ordexa/user") || "null");
      } catch (e) {
        user = null;
      }
      const due = user && user.password_change_due_at;
      if (due && new Date(due) <= new Date() && to.path !== "/personal-profile/password") {
        return next({ path: "/personal-profile/password", query: { obrigatorio: "1" } });
      }
      next();
    } else {
      // Remember a plan link (?plano=) so the checkout opens after signing in.
      try {
        if (to.query.plano) localStorage.setItem("ordexa/pending-plan", String(to.query.plano));
        // Files shared to the app before signing in wait in the service worker: come back to them after login.
        if (to.path === "/compartilhar") localStorage.setItem("ordexa/pending-share", "1");
      } catch (e) {
        // storage blocked: the link just opens the plans page after login
      }
      next({
        path: "/login",
      });
    }
  } else {
    next();
  }
});

// After a deploy, an open tab may ask for a JS chunk that no longer exists (see utils/chunkReload).
router.onError((error, to) => {
  if (isChunkError(error) || /MIME type/i.test((error && error.message) || "")) reloadOnceForChunkError(to.fullPath);
});

export default router;
